import { Box, Flex, HStack, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { SETTINGS_BORDER_COLOR } from './constants';

export interface SettingsCardProps {
    title?: string;
    description?: string;
    /** Rendered on the header row, right aligned. */
    headerAction?: ReactNode;
    /**
     * Remove the inner padding, for cards whose whole body is a table or list
     * that should touch the border.
     */
    isFlush?: boolean;
    /** Draw a divider between each direct child. Defaults to true. */
    dividers?: boolean;
    children: ReactNode;
}

/**
 * A flat surface: white, one hairline border, no shadow.
 *
 * Deliberately a plain Box rather than Chakra's Card so it can never pick up
 * the elevated variant. Shadows in this app mean "floats above the page" and
 * are reserved for overlays.
 */
const SettingsCard = ({
    title,
    description,
    headerAction,
    isFlush = false,
    dividers = true,
    children,
}: SettingsCardProps) => (
    <Box
        bg="white"
        borderWidth="1px"
        borderColor={SETTINGS_BORDER_COLOR}
        borderRadius="lg"
        overflow="hidden"
    >
        {(title || headerAction) && (
            <Flex
                align="flex-start"
                justify="space-between"
                gap={4}
                px={5}
                py={4}
                bg="gray.50"
                borderBottomWidth="1px"
                borderColor={SETTINGS_BORDER_COLOR}
            >
                <Box>
                    {title && (
                        <Text fontSize="md" fontWeight="semibold" color="gray.800">
                            {title}
                        </Text>
                    )}
                    {description && (
                        <Text fontSize="sm" color="gray.500" mt={0.5}>
                            {description}
                        </Text>
                    )}
                </Box>
                {headerAction && (
                    <HStack spacing={2} flexShrink={0}>
                        {headerAction}
                    </HStack>
                )}
            </Flex>
        )}

        <Box
            px={isFlush ? 0 : 5}
            py={isFlush ? 0 : 1}
            sx={
                dividers
                    ? {
                          '& > *:not(:last-child)': {
                              borderBottomWidth: '1px',
                              borderBottomStyle: 'solid',
                              borderBottomColor: 'var(--chakra-colors-gray-200)',
                          },
                      }
                    : undefined
            }
        >
            {children}
        </Box>
    </Box>
);

export default SettingsCard;
